import { useEffect, useRef, useState } from "react";
import { Play } from "@phosphor-icons/react";

// Plays an HLS (.m3u8) stream served straight from the outlet's own CDN.
// Never autoplays: the poster + play button loads the player on tap only.
// Safari plays HLS natively; everything else uses hls.js.
export default function PressVideo({ src, poster, title, testId = "press-video" }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!started) return;
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;

    const play = async () => {
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src;
        video.play().catch(() => {});
        return;
      }
      const { default: Hls } = await import("hls.js");
      if (cancelled) return;
      if (!Hls.isSupported()) {
        setFailed(true);
        return;
      }
      const hls = new Hls({ enableWorker: true });
      hlsRef.current = hls;
      hls.on(Hls.Events.ERROR, (_e, d) => { if (d?.fatal) setFailed(true); });
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
    };
    play();

    return () => {
      cancelled = true;
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
  }, [started, src]);

  return (
    <div data-testid={testId} className="relative w-full aspect-video bg-black overflow-hidden">
      {!started ? (
        <button
          type="button"
          onClick={() => setStarted(true)}
          data-testid={`${testId}-play`}
          aria-label={`Play video: ${title}`}
          className="group absolute inset-0 w-full h-full"
        >
          {poster && (
            <img src={poster} alt={title} className="absolute inset-0 w-full h-full object-cover" />
          )}
          <span className="absolute inset-0 bg-black/35 group-hover:bg-black/20 transition-colors" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#B32027] group-hover:bg-[#D42730] shadow-[0_0_40px_rgba(179,32,39,0.5)] transition-all group-hover:scale-105">
              <Play size={28} weight="fill" className="text-white ml-1" />
            </span>
          </span>
        </button>
      ) : (
        <video
          ref={videoRef}
          controls
          playsInline
          preload="metadata"
          poster={poster}
          data-testid={`${testId}-player`}
          className="absolute inset-0 w-full h-full bg-black"
        />
      )}
      {failed && (
        <div className="absolute inset-x-0 bottom-0 bg-black/85 px-4 py-3 text-center">
          <p className="text-white/80 text-xs">
            The video couldn&rsquo;t load here.{" "}
            <span className="text-white/60">Use the button below to watch it on News 12.</span>
          </p>
        </div>
      )}
    </div>
  );
}
